/**
 * Shared API client — replaces AngularJS `$http` + `api.getApiPath()`.
 *
 * All 434 API service files will use this single client.
 * Handles: base URL, auth headers, JSON parsing, error interceptors.
 */

const API_PATH = import.meta.env.VITE_API_PATH || '/api'

// --- Read-Only Database Opt-Out (matches readOnlyOptOutInterceptor.js) ---
// Tracks the last non-GET call timestamp to add 'APS-ReadOnly-OptOut' header
// within a configured window, preventing stale read-replica scenarios.
let lastNonGetCallTimestamp = 0
let readOnlyOptOutWindowMs = 0

/** Configure the read-only opt-out window. Called once from AppConfigContext. */
export function setReadOnlyOptOutWindow(windowMs: number): void {
    readOnlyOptOutWindowMs = windowMs
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
    params?: Record<string, string | number | boolean | undefined>
    body?: unknown
}

class ApiClientError extends Error {
    status: number
    statusText: string
    data: unknown

    constructor(status: number, statusText: string, data: unknown) {
        super(`API Error: ${status} ${statusText}`)
        this.name = 'ApiClientError'
        this.status = status
        this.statusText = statusText
        this.data = data
    }
}

/** Build full URL with query params */
function buildUrl(urlFragment: string, params?: Record<string, string | number | boolean | undefined>): string {
    let url = `${API_PATH}${urlFragment}`
    if (!params) return url

    const searchParams = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) searchParams.set(key, String(value))
    }
    const qs = searchParams.toString()
    if (qs) url += `?${qs}`
    return url
}

/** Build request headers including auth, read-only opt-out, and extras */
function buildHeaders(extraHeaders?: Record<string, string>): Record<string, string> {
    const token = sessionStorage.getItem('access_token')

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { 'APS-Token': token, Authorization: `Bearer ${token}` } : {}),
        ...extraHeaders,
    }

    // Read-Only Opt-Out header (matches readOnlyOptOutInterceptor.js)
    if (
        readOnlyOptOutWindowMs > 0 &&
        lastNonGetCallTimestamp > 0 &&
        Date.now() - lastNonGetCallTimestamp < readOnlyOptOutWindowMs
    ) {
        headers['APS-ReadOnly-OptOut'] = 'true'
    }

    return headers
}

/** Handle error responses — parse body, log non-excluded errors, throw */
async function handleErrorResponse(response: Response, method: string, urlFragment: string): Promise<never> {
    const rawText = await response.text()
    let errorData: unknown
    try { errorData = JSON.parse(rawText) } catch { errorData = rawText }

    const error = new ApiClientError(response.status, response.statusText, errorData)

    // Error reporting (matches ajaxErrorInterceptor.js) — excluded: 400, 401, 402, 403, 412
    const excluded = [400, 401, 402, 403, 412]
    if (!excluded.includes(response.status)) {
        console.error(`[apiClient] ${method} ${urlFragment} → ${response.status}`, errorData)
    }

    throw error
}

/** Parse successful response body */
async function parseResponseBody<T>(response: Response, method: string): Promise<T> {
    if (response.status === 204) return undefined as T

    const rawText = await response.text()

    // Empty response interceptor (matches defaultHttpResponseInterceptor.js)
    if (rawText === '' && method.toUpperCase() === 'GET') return {} as T

    try { return JSON.parse(rawText) as T } catch { return rawText as T }
}

async function request<T>(
    method: string,
    urlFragment: string,
    options: RequestOptions = {},
): Promise<T> {
    const { params, body, headers: extraHeaders, ...fetchOptions } = options

    const url = buildUrl(urlFragment, params)
    const headers = buildHeaders(extraHeaders as Record<string, string>)

    const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        ...fetchOptions,
    })

    // 401 Interceptor (matches security/interceptor.js)
    if (response.status === 401) {
        sessionStorage.removeItem('access_token')
        globalThis.location.hash = '#/login'
        throw new ApiClientError(401, response.statusText, null)
    }

    // Track non-GET call timestamps for read-only opt-out (matches readOnlyOptOutInterceptor.js)
    // Only update if the response was NOT served from the read-only replica (checks aps-readonly-request header)
    if (method.toUpperCase() !== 'GET') {
        const isReadOnly = response.headers?.get('aps-readonly-request') === 'true'
        if (!isReadOnly) {
            lastNonGetCallTimestamp = Date.now()
        }
    }

    if (!response.ok) {
        return handleErrorResponse(response, method, urlFragment)
    }

    return parseResponseBody<T>(response, method)
}

/** Shared API client — drop-in replacement for `$http` + `api.getApiPath()` */
export const apiClient = {
    get: <T>(url: string, options?: RequestOptions) =>
        request<T>('GET', url, options),

    post: <T>(url: string, body?: unknown, options?: RequestOptions) =>
        request<T>('POST', url, { ...options, body }),

    put: <T>(url: string, body?: unknown, options?: RequestOptions) =>
        request<T>('PUT', url, { ...options, body }),

    patch: <T>(url: string, body?: unknown, options?: RequestOptions) =>
        request<T>('PATCH', url, { ...options, body }),

    delete: <T>(url: string, options?: RequestOptions) =>
        request<T>('DELETE', url, options),

    /** Get the full API path — matches the legacy `api.getApiPath()` */
    getApiPath: (urlFragment: string) => `${API_PATH}${urlFragment}`,
}

export { ApiClientError }
export type { RequestOptions }
